/**
 * Hero Component
 * Renders the main homepage hero banner featuring a background photo slideshow,
 * high-level value proposition, community stats, and navigation hub cards
 * to the Community Trust and Social Club portals.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Hero.css';
import { getSiteContent } from '../../utils/siteContent';

/**
 * Dynamically loads and sorts image assets from a Webpack require context.
 * @param {Function} context - Webpack require.context
 * @returns {Array<string>} Array of image file paths
 */
const loadSlideImages = (context) => {
  return context
    .keys()
    .sort((a, b) => a.localeCompare(b))
    .map((key) => context(key));
};

// Dynamically import homepage slideshow images from part1 and part2 asset directories
const part1Context = require.context('../../assets/part1', false, /\.(png|jpe?g|webp|avif)$/i);
const part2Context = require.context('../../assets/part2', false, /\.(png|jpe?g|webp|avif)$/i);
const HERO_SLIDES = [...loadSlideImages(part1Context), ...loadSlideImages(part2Context)];

const Hero = () => {
  const gridRef = useRef(null); // Tracks the hub cards grid for animation
  const hubSectionRef = useRef(null);
  const slideIntervalRef = useRef(null);
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [siteContent, setSiteContent] = useState(() => getSiteContent());

  // Automatic slideshow rotation effect (4.5s interval)
  useEffect(() => {
    if (HERO_SLIDES.length <= 1) return;

    slideIntervalRef.current = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % HERO_SLIDES.length);
    }, 4500);

    return () => {
      if (slideIntervalRef.current) {
        window.clearInterval(slideIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleContentUpdate = () => setSiteContent(getSiteContent());
    window.addEventListener('site-content-updated', handleContentUpdate);
    return () => window.removeEventListener('site-content-updated', handleContentUpdate);
  }, []);

  // IntersectionObserver effect for staggered fade-up animation of hub cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-up-active');
            // Unobserve after animating so it doesn't repeat
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    // Apply observer to each card and stagger their start times
    if (gridRef.current) {
      const cards = gridRef.current.querySelectorAll('.fade-up');
      cards.forEach((card, index) => {
        // Stagger the animation using CSS transition delay
        card.style.transitionDelay = `${index * 0.15}s`;
        observer.observe(card);
      });
    }

    // Cleanup observer on component unmount
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <>
      {/* Centered Hero Layout with Background Photo Slideshow */}
      <section className="hero-centered-trust">
        <div className="hero-centered-bg">
          {HERO_SLIDES.map((slideSrc, index) => (
            <div
              key={`${slideSrc}-${index}`}
              className={`hero-centered-slide ${index === activeSlide ? 'is-active' : ''}`}
              style={{ backgroundImage: `url(${slideSrc})` }}
              aria-hidden={index !== activeSlide}
            />
          ))}
          {/* Slideshow Navigation Dots */}
          <div className="hero-slide-dots hero-centered-dots" aria-label="Hero slideshow position">
            {HERO_SLIDES.map((_, index) => (
              <button
                key={`hero-dot-${index}`}
                type="button"
                className={`hero-slide-dot ${index === activeSlide ? 'is-active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show launch slide ${index + 1}`}
                aria-pressed={index === activeSlide}
              />
            ))}
          </div>
          <div className="hero-centered-overlay"></div>
        </div>
        
        {/* Value Proposition Content & Statistics */}
        <div className="hero-centered-content">
          <div className="hero-value-prop centered">
            <h1>{siteContent.home.heroTitle}</h1>
            <p>{siteContent.home.heroDescription}</p>
          </div>

          <div className="hero-stats centered-stats">
            <div className="stat-item">
              <span className="stat-number">{siteContent.home.heroStatNumber}</span>
              <span className="stat-label">{siteContent.home.heroStatLabel}</span>
            </div>
          </div>
        </div>
      </section>
      
      {/* Bottom Navigation Grid linking to Community Trust and Social Club */}
      <section id="hub-section" className="hub-section" ref={hubSectionRef}>
        <div className="hero-content">
          <div className="hub-grid" ref={gridRef}>
            <Link id="hero-trust-card" to="/trust" className="hub-card card-trust fade-up">
              <h3>{siteContent.home.trustCardTitle}</h3>
              <p>{siteContent.home.trustCardDescription}</p>
              <span className="hub-link-text">Visit &rarr;</span>
            </Link>
            
            <Link id="hero-social-card" to="/social" className="hub-card card-social fade-up">
              <h3>{siteContent.home.socialCardTitle}</h3>
              <p>{siteContent.home.socialCardDescription}</p>
              <span className="hub-link-text">Visit &rarr;</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
